import { editorHasBlockWithType } from '@blocknote/core'
// @ts-expect-error Subpath export resolves at runtime via webpack; not under moduleResolution "node".
import { SideMenuExtension } from '@blocknote/core/extensions'
import {
  blockTypeSelectItems,
  useBlockNoteEditor,
  useComponentsContext,
  useExtensionState,
} from '@blocknote/react'
import React, { useMemo } from 'react'

type TurnIntoMenuItemProps = {
  children: React.ReactNode
}

const TurnIntoMenuItem = ({ children }: TurnIntoMenuItemProps) => {
  const Components = useComponentsContext()!
  const editor = useBlockNoteEditor()

  const block = useExtensionState(SideMenuExtension, {
    editor,
    selector: (state) => state?.block,
  })

  const items = useMemo(() => {
    if (!block) return []

    return blockTypeSelectItems(editor.dictionary).filter((item) =>
      editorHasBlockWithType(
        editor,
        item.type,
        Object.fromEntries(
          Object.entries(item.props ?? {}).map(([propName, propValue]) => [
            propName,
            typeof propValue,
          ])
        ) as Record<string, 'string' | 'number' | 'boolean'>
      )
    )
  }, [block, editor])

  if (!block || items.length === 0) return null

  const selectedBlocks = editor.getSelection()?.blocks
  const blocksToUpdate =
    selectedBlocks && selectedBlocks.some((b) => b.id === block.id)
      ? selectedBlocks
      : [block]

  return (
    <Components.Generic.Menu.Root position="right" sub>
      <Components.Generic.Menu.Trigger sub>
        <Components.Generic.Menu.Item className="bn-menu-item" subTrigger>
          {children}
        </Components.Generic.Menu.Item>
      </Components.Generic.Menu.Trigger>

      <Components.Generic.Menu.Dropdown sub className="bn-menu-dropdown">
        {items.map((item) => {
          const Icon = item.icon
          const typesMatch = item.type === block.type
          const propsMatch =
            Object.entries(item.props ?? {}).filter(
              ([propName, propValue]) =>
                propValue !== (block.props as Record<string, unknown>)[propName]
            ).length === 0

          return (
            <Components.Generic.Menu.Item
              key={`${item.type}-${item.name}`}
              className="bn-menu-item"
              icon={<Icon size={18} />}
              checked={typesMatch && propsMatch}
              onClick={() => {
                editor.focus()
                editor.transact(() => {
                  for (const targetBlock of blocksToUpdate) {
                    editor.updateBlock(targetBlock, {
                      type: item.type as typeof block.type,
                      props: item.props as typeof block.props,
                    })
                  }
                })
              }}
            >
              {item.name}
            </Components.Generic.Menu.Item>
          )
        })}
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  )
}

export default TurnIntoMenuItem
